/**
 * Purchasing → ASN tab: the filter-panel state and its mapping onto the
 * `/purchasing/asns/` query string.
 *
 * Extracted from `ASNTab` so the mapping is a pure function with its own
 * test. CR-6441 client re-test (7 Sep): Division and Company Country were
 * offered in the Company section and narrowed the Site dropdown, but neither
 * was ever sent to the API — the list did not change unless a Site was also
 * picked. The backend has declared `division` and `customer__country` since
 * the rework; they are now sent.
 */

/** Empty filter state — one key per control on either layout. */
export const ASN_EMPTY_FILTERS = {
  asnNumber: '',
  partSearch: '',
  supplier: '',
  trackingNumber: '',
  poNumber: '',
  poLineNumber: '',
  partNumber: '',
  description: '',
  status: '',
  courier: '',
  courierDeliveryStatus: '',
  dateFrom: '',
  dateTo: '',
  site: '',
  warehouse: '',
  supplierCountry: '',
  supplierGroup: '',
  commodityGroup: '',
  commodityType: '',
  strategic: '',
  // CR-6441: Company section cascade fields
  division: '',
  companyCountry: '',
  // CR-6441: fields per the design
  supplierCode: '',
  buyerId: '',
  // Supplier Type (internal / external) — customer view only.
  supplierType: '',
};

export type ASNAppliedFilters = typeof ASN_EMPTY_FILTERS;

export type ASNQueryParams = Record<string, string | number | boolean>;

/**
 * Text and select controls that map one-to-one onto a filterset param.
 * Order matches the panel, top to bottom.
 */
const DIRECT_PARAMS: ReadonlyArray<[keyof ASNAppliedFilters, string]> = [
  ['asnNumber', 'asn_number__icontains'],
  // Quick box: one term, matched against the part number OR the supplier's
  // own reference on any line of the ASN.
  ['partSearch', 'part_search'],
  ['supplier', 'supplier__name__icontains'],
  ['trackingNumber', 'tracking_number__icontains'],
  ['courier', 'courier'],
  ['dateFrom', 'expected_delivery_date__gte'],
  ['dateTo', 'expected_delivery_date__lte'],
  ['poNumber', 'schedules__po_line__po__po_number__icontains'],
  ['poLineNumber', 'schedules__po_line__line_number'],
  // Company section. `division` expands to every site under the group on
  // the backend; `customer__country` is the ASN customer's country.
  ['division', 'division'],
  ['companyCountry', 'customer__country'],
  ['site', 'schedules__po_line__po__customer'],
  // CR-6449: value is the warehouse code — the backend matches the code an
  // ASN was booked against, not a warehouse id.
  ['warehouse', 'warehouse_code'],
  ['supplierGroup', 'supplier__group'],
  ['supplierCountry', 'supplier__country'],
  ['supplierCode', 'supplier_code'],
  // Supplier Type → the filterset's supplier_type (shared internal-suppliers helper).
  ['supplierType', 'supplier_type'],
  ['commodityGroup', 'schedules__po_line__commodity_group'],
  ['commodityType', 'schedules__po_line__commodity_type'],
  ['buyerId', 'schedules__po_line__buyer_id__icontains'],
  ['partNumber', 'schedules__po_line__part_number__icontains'],
  ['description', 'schedules__po_line__description__icontains'],
  ['courierDeliveryStatus', 'courier_delivery_status'],
];

/**
 * Map the applied filter state (plus the active status tab) onto the ASN
 * list's query params. Empty values are omitted; the caller adds paging,
 * ordering and the view-context scope.
 *
 * @param applied - The applied (not draft) filter state.
 * @param activeTab - The status tab: 'all', a status value, or
 *   'direct_acknowledged' (CR-7142, not a status).
 * @returns Query params for `purchasingAPI.getASNs`.
 */
export function buildASNQueryParams(applied: ASNAppliedFilters, activeTab: string): ASNQueryParams {
  const params: ASNQueryParams = {};

  for (const [key, param] of DIRECT_PARAMS) {
    const value = applied[key];
    if (value) params[param] = value;
  }

  // Status: the dropdown wins over the tab.
  if (applied.status) {
    params['status'] = applied.status;
  } else if (activeTab === 'direct_acknowledged') {
    params['direct_acknowledged'] = true;
  } else if (activeTab !== 'all') {
    params['status'] = activeTab;
  }

  if (applied.strategic === 'yes') {
    params['strategic_only'] = 'yes';
  }

  return params;
}
